package top.wcpe.allincore.ui.runtime.render.layer

import kotlin.jvm.JvmField
import kotlin.jvm.JvmName

/**
 * 绘制时刻的渲染状态快照：变换栈 + 透明度栈 + 色彩矩阵栈 + 裁剪区域。
 *
 * 不可变对象：构造时对各列表做只读拷贝、对裁剪数组做深拷贝。
 * 注意：不包含 z 偏移——层缓冲模式下层序已解决跨层遮挡。
 *
 * 访问器经 [JvmName] 保留 record 风格的 `transformStack()` / `currentClip()` 等 Java 方法 ABI。
 *
 * **栈序约定（从顶到底）**：各栈列表的**首元素为最新入栈项**——快照由 `StateStacks` 的
 * `ArrayDeque.addFirst` + 自队头迭代产出（见 `StateStacks.snapshot` KDoc），
 * `LayerBufferingBridge.pushStateToDelegate` 按此列表序推入、`popStateFromDelegate` 逆序弹出。
 *
 * @param transformStack   变换栈（首元素为最新入栈项）
 * @param alphaStack       透明度栈（首元素为最新入栈项），空栈表示所有层均为默认 1.0
 * @param colorMatrixStack 色彩矩阵栈（首元素为最新入栈项），每项 4×5 共 20 元素行优先数组；空栈表示无 filter
 * @param currentClip      当前裁剪区域 [x, y, w, h]（已含层叠求交），null 表示无裁剪
 */
class StateSnapshot(
    transformStack: List<TransformEntry>,
    alphaStack: List<Float>,
    colorMatrixStack: List<FloatArray>,
    currentClip: FloatArray?,
) {

    @get:JvmName("transformStack")
    val transformStack: List<TransformEntry> = transformStack.map { entry ->
        when (entry) {
            is TransformEntry.TranslateScale -> entry
            is TransformEntry.Matrix3x3 -> TransformEntry.Matrix3x3(entry.mat)
            is TransformEntry.Matrix4x4 -> TransformEntry.Matrix4x4(entry.mat)
        }
    }

    /** 完整透明度栈（从底到顶），空栈表示所有层均为默认 1.0。 */
    @get:JvmName("alphaStack")
    val alphaStack: List<Float> = alphaStack.toList()

    /** 完整色彩矩阵栈（从底到顶），每项 4×5 共 20 元素；空栈表示无 filter。 */
    @get:JvmName("colorMatrixStack")
    val colorMatrixStack: List<FloatArray> = colorMatrixStack.map(FloatArray::copyOf)

    /** 当前裁剪区域 [x, y, w, h]，null 表示无裁剪。 */
    @get:JvmName("currentClip")
    val currentClip: FloatArray? = currentClip?.copyOf(CLIP_SIZE)

    /** 旧三参构造：无 colorMatrixStack。 */
    @Deprecated("使用四参主构造 StateSnapshot(transformStack, alphaStack, colorMatrixStack, currentClip)")
    constructor(
        transformStack: List<TransformEntry>,
        alphaStack: List<Float>,
        currentClip: FloatArray?,
    ) : this(transformStack, alphaStack, emptyList(), currentClip)

    /** 旧三参构造：以单一 alpha 值表达透明度（非默认值时入栈）。 */
    @Deprecated("使用四参主构造 StateSnapshot(transformStack, alphaStack, colorMatrixStack, currentClip)")
    constructor(
        transformStack: List<TransformEntry>,
        currentAlpha: Float,
        currentClip: FloatArray?,
    ) : this(
        transformStack,
        if (currentAlpha != DEFAULT_ALPHA) listOf(currentAlpha) else emptyList(),
        emptyList(),
        currentClip,
    )

    /** 栈顶透明度，空栈回退默认 1.0。 */
    @Deprecated("使用 alphaStack() 获取完整透明度栈", ReplaceWith("alphaStack().lastOrNull() ?: 1.0f"))
    @get:JvmName("currentAlpha")
    val currentAlpha: Float
        get() = alphaStack.lastOrNull() ?: DEFAULT_ALPHA

    companion object {
        /** 裁剪区域固定为 4 元素 [x, y, w, h]。 */
        private const val CLIP_SIZE = 4

        /** 默认透明度（不透明）。 */
        private const val DEFAULT_ALPHA = 1.0f

        /** 空状态（无变换、无透明度、无色彩矩阵、无裁剪）。 */
        @JvmField
        val EMPTY = StateSnapshot(emptyList(), emptyList(), emptyList(), null)
    }
}
